import type { FeatureTag } from "./matter/features"

/** Hand-mirrored TS types for backend/nira/nira/agent/contract.py (see contract.gen.json). */

export const MASCOT_STATES = ["peek", "listening", "searching", "asking", "section", "done", "error", "idle"] as const
export type MascotState = (typeof MASCOT_STATES)[number]

export const EVENT_TYPES = [
  "turn.started", "step", "clarify", "answer.head", "sections.plan", "section", "sources",
  "answer.done", "turn.error", "doc.progress", "doc.ready", "doc.error",
] as const
export type EventType = (typeof EVENT_TYPES)[number]

export const BLOCK_TYPES = [
  "para", "disclosure", "quote", "list", "table", "note", "ask_back", "tally", "checklist", "your_use",
  "turns_on", "limitation", "decisions", "exposure", "deadline",
] as const

export const DEFAULT_MASCOT: Record<EventType, MascotState | null> = {
  "turn.started": "searching", step: "searching", clarify: "asking", "answer.head": "searching",
  "sections.plan": "searching", section: "section", sources: null, "answer.done": "done",
  "turn.error": "error", "doc.progress": null, "doc.ready": null, "doc.error": null,
}

export const DOC_STATUSES = ["queued", "rendering", "indexing", "summarising", "ready", "failed"] as const
export type DocStatus = (typeof DOC_STATUSES)[number]

/** Common envelope the server adds to every payload. */
export interface Envelope { seq: number; t: number; mascot: MascotState | null }

export type StepStatus = "pend" | "run" | "done" | "error"
export type StepKind = "read" | "limit" | "search" | "found" | "read_source" | "verify" | "write" | "doc"
export interface Step { id: string; label: string; detail: string; status: StepStatus; kind: StepKind }

export interface ClarifyField { name: string; label: string; options: string[]; allow_other: boolean }
export interface Clarify { title: string; why: string; fields: ClarifyField[]; can_assume: boolean }

export interface MatterContext {
  state?: string | null; period?: string | null; section?: string | null
  notice_date?: string | null; amount_inr?: number | null
  /** The notice or order form: "DRC-01", "ASMT-10". */
  form?: string | null
}
/** Which way the answer leans for the reader's side; the counts come from the decisions blocks. */
export interface Lean { toward: "helps" | "hurts" | "mixed"; line: string }
export interface AnswerHead {
  /** A short name for the matter ("DRC-01, supplier non-filing"); names the thread. */
  title?: string
  short: string; underline?: { start: number; end: number } | null; lede: string
  context?: MatterContext | null; assumptions: string[]; lean?: Lean | null
}
export type SectionProp = "calendar" | "book" | "court" | "coin" | "checklist"
export interface SectionPlanItem { id: string; tag: string; heading: string; takeaway: string; prop: SectionProp; read_now?: boolean }

export interface Cite { n: number; source_id: string }
export interface AskBackOption { value: string; label: string; sentence: string; takeaway: string }
export interface DecisionItem { source_id: string; scope: string; holding: string; binds?: boolean }
export interface ExposureRow { label: string; amount_inr?: number | null; note?: string }
export interface TallyCase { name: string; court: string; year?: number | null; outcome: string; source_id: string }
export type Block =
  | { type: "para"; text: string; cites: Cite[] }
  | { type: "disclosure"; label: string; blocks: Block[] }
  | { type: "quote"; source_id: string; para: number; before: string; phrase: string; after: string }
  | { type: "list"; ordered: boolean; items: string[] }
  | { type: "table"; rows: [string, string][]; note: string }
  | { type: "note"; text: string }
  | { type: "ask_back"; question: string; options: AskBackOption[] }
  | { type: "tally"; won: number; remanded: number; lost: number; total: number; cases: TallyCase[] }
  | { type: "checklist"; items: string[] }
  | { type: "your_use"; for: string[]; against: string[] }
  | { type: "turns_on"; items: { label: string; question: string }[] }
  | { type: "limitation"; verdict: "late" | "in_time" | "unknown"; summary: string; rows: { label: string; value: string; basis?: string }[]; caveats?: string[] }
  | { type: "decisions"; lean: "helps" | "hurts"; items: DecisionItem[]; none?: string }
  | { type: "exposure"; rows: ExposureRow[]; total?: ExposureRow | null; monthly_interest_inr?: number | null; note?: string }
  | { type: "deadline"; label?: string; date: string; basis?: string }

export interface Section { id: string; blocks: Block[] }

/** Optional text fields may be null (treat null as "hide"). */
export interface Source {
  id: string
  kind: "case" | "law" | "doc"
  label: string
  /** The Nira document id of a case ("sc:2023INSC212"): what a reader's vote on its result is recorded against. */
  doc_id?: string | null
  verified: boolean
  verified_label?: string | null
  name?: string | null
  court?: string | null
  date?: string | null
  citation?: string | null
  status?: string | null
  status_checked?: string | null
  binds?: string | null
  cited_para?: number | null
  relied?: { before: string; phrase: string; after: string } | null
  paras: { n: number; text: string; cited: boolean }[]
  full_text_url?: string | null
  doc?: { doc_id: string; page: number; start_line: number; end_line: number } | null
}

export interface SearchTrail { steps: { t: number; label: string }[]; total_sec: number }
/** A decision the searches found this turn; `source_id` set when Nira read it. */
export interface FoundDecision {
  doc_id: string
  title: string
  court: string
  year: number | null
  outcome: string
  snippet: string
  source_id: string | null
}
export type TurnErrorCode = "tool_unavailable" | "model_error" | "timeout" | "limit" | "internal"
export interface TurnError { code: TurnErrorCode; message: string; retryable: boolean; partial: boolean }

export interface DocRow { doc_id: string; filename: string; status: DocStatus; size_bytes: number; error?: string | null }
export interface DocProgress {
  doc_id: string; filename: string; pages_total: number; pages_done: number; pages_failed: number; status: string
}

export type TurnMode = "ask" | "draft_check"
export interface TurnRequest {
  message: string
  mode: TurnMode
  doc_ids: string[]
  answers?: Record<string, unknown>
  /** Read this one turn for a side without moving the thread's chip (the department's reading). */
  side?: "taxpayer" | "revenue"
}
/** The feature a row belongs to, as the server tags it (see matter/features.ts for the client names). */
export type { FeatureTag }

export interface ThreadRow {
  id: string
  title: string
  /** Which feature the thread is, when the server tags it. */
  feature?: FeatureTag
  updated_at?: string | null
  context?: { side: string; state_code: string; state?: string }
  /** The case file this thread belongs to, when it is in one. */
  case?: { id: string; title: string } | null
}

/** One notice or appeal reading across the reader's workspaces (GET /v2/readings/recent), for the history drawer. */
export interface RecentReading {
  id: string
  case_id: string
  kind: string
  feature: FeatureTag
  status: "running" | "done" | "failed"
  /** The workspace's title. */
  title: string
  updated_at: string
}

/** What the notice says, for the case file's header line; any field may be empty. */
export interface CaseFacts {
  state_code: string
  state: string
  section: string
  periods: string[]
  proposed_total: number | null
  reply_due: string
  reference: string
  /** The document's own title as the model summarised it; the case file's title is set by code. */
  doc_title?: string
}

/** A case file: one matter, kept across research, notice analysis and appeal. */
export interface CaseFileRow {
  id: string
  title: string
  facts: CaseFacts
  keep_after_close: boolean
  allow_training: boolean
  created_at: string
  updated_at: string
  closed: boolean
  /** The reader has checked and saved the notice's facts. */
  notice_confirmed?: boolean
  /** Folded into an earlier case file for the same document: open that one instead. */
  merged_into?: string | null
  /** Which feature the workspace began as, when the server tags it. */
  feature?: FeatureTag
}

/** A case file in the list: with how many threads and documents it holds. */
export interface CaseFileListRow extends CaseFileRow {
  threads: number
  documents: number
  /** What the matter began with: "notice", "order", or "" before a document is read. */
  lead?: string
  /** Where it stands: its latest reading, if any. */
  latest_reading?: { id: string; kind: string; status: "running" | "done" | "failed" } | null
}

export interface CaseDocument {
  id: string
  filename: string
  /** notice, reply, order, ewb, invoice, bank, cancellation, lr, gstr3b, hearing, other */
  kind: string
  title: string
  pages: number
  status: string
  /** Identifiers masked in the browser before upload; null when not masked on device. */
  masked_count: number | null
  thread_id: string | null
  created_at: string
}

export interface CaseThread { id: string; title: string; turns: number; last_question: string; created_at: string; updated_at: string }
export interface CaseReading {
  id: string
  kind: string
  session_id: string | null
  /** The documents it read. */
  inputs?: string[]
  created_at: string
  runs: number
  status?: "running" | "done" | "failed"
  lead?: { line: string; sub: string } | null
  /** The officer's reading on a notice reading, once asked for: whether it ran, and what likely stands. */
  officer?: { status: string; line: string } | null
}

/** One stage of the working page, landing in order. */
export interface ReadingStage { key: string; label: string; done: boolean; line: string }

export type YearStatus = "out" | "turns_on_intent" | "in_time" | "unknown"

/** Before the notice: when each allegation first showed in data the taxpayer held (nira/law/exposure.py). */
export interface ExposureHint {
  line: string
  visible_since: string
  signals: { id: string; signal: string; where: string; visible_since: string; line: string }[]
  working: string
}

/** Whether runs agree: n runs of `of` reached the call shown. */
export interface Agreement { n: number; of: number }

export type CallStatus = "confirmed" | "partly" | "dropped" | "falls_with_year" | "counted_with"

/**
 * One allegation in the officer's reading (nira/notice/officer.py). `kind: "code"` cards are worked
 * out (a fallen year, an alternative pair); `kind: "reading"` cards carry the engine's call, three
 * runs, with agreement counted by code. The call is a reading, marked as such, never a probability.
 */
export interface OfficerCard {
  id: string
  amount: number
  kind: "code" | "reading"
  call: {
    status: CallStatus
    word: string
    line?: string
    because?: "merits" | "intent"
    agreement?: Agreement
    consistent?: boolean
    /** No call has most of the runs: the card says it could go either way. */
    split?: boolean
    why?: string
    why_split?: string
  } | null
  missing?: string
  strongest_point?: string
  best_answer?: string
  would_change?: string[]
  /** The finding as the order would put it, in the officer's voice (section 06). */
  order_finding?: string
  pages?: number[]
  /** s.75(2), by code: what happens to this allegation if intent fails. */
  if_intent_fails?: { kind: "code"; line: string; fresh_s73?: FreshS73[] } | null
}

export interface FreshS73 { fy: string; possible: boolean; until: string }

/** Section 03: intent under s.74 (or the penalty limb under s.74A), and what turns on it. */
export type OfficerIntent =
  | { pleaded: false; line: string }
  | {
      pleaded: true
      their_words: string
      holds: boolean | null
      word: string
      agreement: Agreement
      consistent: boolean
      why: string
      act_relied_on: string
      /** When runs split: each side, in its own words. */
      sides: { made_out: boolean; n: number; why: string; act_relied_on: string }[]
      /** Every verdict among the runs, counted, each in one line; the reasons are its working. */
      runs?: { made_out: boolean; n: number; line: string; why: string }[]
      if_holds: { tax: number; section: string; penalty_rate: number; line: string; working: string }
      if_fails: { tax: number; section: string; line: string; fresh_s73: FreshS73[]; fresh_line: string; working: string }
    }

/** The paid slots of a notice reading, kept on the reading once run (`contract.officer`). */
export interface OfficerReading {
  status: "running" | "done" | "failed"
  error?: string
  runs: { done: number; of: number }
  engine?: string
  mode?: "no_reply"
  allegations?: OfficerCard[]
  intent?: OfficerIntent | null
  /** Tax likely to stand: a range when runs split. Code over the calls. */
  standing?: { low: number; high: number; line: string; working: string }
  meta?: { seconds: number; run_seconds: number[]; input_tokens: number; output_tokens: number; usd: number; retried: number; unresolved: number; model: string }
}

/** The free reading (NA-Preview), worked out by code from the confirmed notice (nira/notice/preview.py). */
export interface FreeReading {
  mode: "no_reply"
  tier: "preview"
  computed_on: string
  header: {
    notice_number: string; notice_date: string; din: string; authority: string; section: string
    years: string[]; reply_due: string; hearing_offered: boolean | null; proposed: number
  }
  lead: { line: string; sub: string }
  glance: {
    time_limit: { years_out: number; of: number; falls: number; line: string }
    allegations: { live: number; paired: number; fall: number; line: string }
    worst: { amount: number; line: string }
    reply_by: { date: string; line: string }
  }
  validity: {
    years: { fy: string; status: YearStatus; status_word: string; line: string; working: string; contested: boolean }[]
    checks: { key: "din" | "hearing"; status: "stands" | "check" | "ask"; line: string; working: string }[]
    summary: string
  }
  allegations: {
    id: string; title: string; periods: string[]; years: string[]; amount: number
    status: "live" | "partly_out" | "falls_with_year" | "counted_with"; note: string; paired_with: string | null
    /** The department's own words on this allegation, as printed. */
    they_say?: string
    /** The records this kind of charge turns on, matched by code from its words. */
    evidence?: AllegationEvidence | null
  }[]
  money: {
    proposed: number
    in_play: number
    worst: { amount: number; working: string; line: string }
    pay_now: { amount: number; open: boolean; window_ends: string; working: string; line: string }
    interest: { amount: number; working: string }
    interest_per_month: { amount: number; working: string }
  }
  actions: { reply_by: string; days_left: number | null; hearing_to_ask: boolean; line: string }
  limits: string[]
  exposure_hint?: ExposureHint | null
  /** What Nira read with doubt; shown on the reading, never asked before it. */
  unsure?: { field: string; why: string }[]
  /** The officer's reading, once run: the paid slots (sections 02, 03, 04 and 06). */
  officer?: OfficerReading | null
}

/** Appeal or not from the order alone (NA-Appeal), worked out by code (nira/order/appeal.py). */
export interface AppealReading {
  mode: "order_preview"
  tier: "preview"
  computed_on: string
  header: {
    order_number: string; order_date: string; communicated_on: string; din: string; authority: string
    section: string; years: string[]; tax: number; interest: number; penalty: number; notice_reference: string
    communicated_from_reader?: boolean
    appellate?: boolean
    matter?: "demand" | "refund" | "other"
  }
  lead: { line: string; sub: string }
  deadlines: {
    appeal_by: string; with_cause_until: string; days_left: number | null; working: string
    basis?: string; deposit_basis?: string
    pre_deposit: { amount: number; working: string; tribunal_further: number }
    forum: { name: string; working: string; next: string }
    reduced_penalty: { until: string; open: boolean; amount: number; working: string } | null
  }
  findings: { id: string; title: string; outcome: "confirmed" | "partly" | "dropped"; years: string[]; tax: number | null; penalty: number | null; they_find: string; para: string }[]
  points: { kind: string; ground_type: string; needs: string; text: string; source: string; working: string }[]
  needs_more: { what: string; detail: string; status: "ready" | "partly" | "needs"; add: string }[]
  options: { appeal: { amount: number; line: string }; rectify: { line: string }; pay: { amount: number; line: string } }
  not_predicted: string
  next_step?: { title: string; line: string; status: "soon" | "open" }
  exposure_hint?: ExposureHint | null
  actions: { steps: { text: string; by: string }[]; interest_per_month: number }
  limits: string[]
}

/** How much of a reading the reader is given: their free one in full, a Pro one, or the preview. */
export type ReadingLevel = "free" | "full" | "preview"

export interface ReadingView {
  id: string
  case_id: string
  /** The workspace's feature, when the server tags it. */
  feature?: FeatureTag
  kind: string
  status: "running" | "done" | "failed"
  /** Absent from servers before handoff 3: read as full. */
  level?: ReadingLevel
  contract: (Partial<FreeReading> | Partial<AppealReading>) & { stages?: ReadingStage[]; error?: string; mode?: string; lead?: { line: string; sub: string } }
  created_at: string
  updated_at: string | null
  case?: CaseFileRow & { notice_confirmed: boolean }
}
export interface CaseAsk { id: string; allegation: string; document_kind: string; what: string; rupees_moved: number | null; status: "open" | "received" | "checked" }

/** Rupees by head as printed; null when the notice does not demand that head. */
export interface TaxHeads { cgst: number | null; sgst: number | null; igst: number | null; cess: number | null }

/** One allegation as Nira read it (nira/notice/read.py view_of). */
export interface NoticeAllegation {
  id: string
  title: string
  they_say: string
  periods: string[]
  years: string[]
  sections: string[]
  tax: TaxHeads
  tax_total: number | null
  interest: number | null
  penalty: number | null
  pages: number[]
  /** The earlier allegation this one is the same money as; totals count the pair once. */
  paired_with: string | null
}

/** What Nira read from the notice, plus what code worked out from it. */
export interface NoticeRead {
  kind: "show_cause_notice" | "drc01a" | "asmt10" | "order" | "order_in_appeal" | "reply" | "other"
  kind_words: string
  is_notice: boolean
  filename: string
  notice_number: string
  notice_date: string
  din: string
  authority: string
  gstin: string
  section_invoked: "73" | "74" | "74A" | ""
  fraud_pleaded: boolean
  suppression_recital: string
  periods: string[]
  years: string[]
  reply_due: string
  reply_due_as_printed: string
  hearing: { offered: boolean | null; date: string }
  noticees: number
  /** Present when the document is an order: the second read, for Appeal or not. */
  order?: unknown
  order_error?: string
  allegations: NoticeAllegation[]
  totals: { tax: number | null; interest: number | null; penalty: number | null; pairs: number }
  unsure: { field: string; why: string }[]
}

/** The facts the reader checked on the confirm step. */
export interface ConfirmedNotice {
  notice_number: string
  notice_date: string
  din: string
  authority: string
  section_invoked: "73" | "74" | "74A" | ""
  periods: string[]
  reply_due: string
  hearing_offered: boolean | null
  allegations: { id: string; title: string; periods: string[]; tax: TaxHeads; interest: number | null; penalty: number | null }[]
}

export interface NoticeState {
  case: CaseFileRow & { notice_confirmed: boolean }
  status: "none" | "reading" | "ready" | "failed"
  document: CaseDocument | null
  read: NoticeRead | null
  confirmed: ConfirmedNotice | null
  /** The first page as uploaded (masked on the device when the reader chose to). */
  preview: string[]
}

export interface CaseFileDetail extends CaseFileRow {
  documents: CaseDocument[]
  threads: CaseThread[]
  readings: CaseReading[]
  asks: CaseAsk[]
}

/** The three places a Matter's sessions live: a Nira search, an OIO draft, an OIA draft. */
export type MatterSessionType = "nira" | "oio" | "oia"

/** One file on a Matter's shelf (C1 catalog entry, as Nira sends it). */
export interface MatterFile {
  doc_id: string
  name: string
  /** Nira's vocabulary: notice, reply, order, ewb, invoice, bank, cancellation, lr, gstr3b, hearing, other. */
  kind: string
  /** YYYY-MM-DD: the date found in the summary, else the upload date. */
  date: string
  summary: string
  origin: {
    type: MatterSessionType | "matter"
    session_id: string | null
    session_title: string
    added_at: string
  }
  /** Other sessions holding the same file, oldest first; absent or empty when unique. */
  also_in?: MatterFile["origin"][]
  status: "queued" | "rendering" | "indexing" | "summarising" | "ready" | "failed"
  pages_total: number
  pages_done: number
  /** Why the file could not be read; set only when status is "failed". */
  error?: string | null
  sha256: string
  size_bytes: number
  /** The document's own reference fields, found when it is read. Absent from an older backend. */
  ref?: DocRef | null
  /** The proceeding (its anchor's doc_id) this file belongs to; null or absent: not linked. */
  proceeding_id?: string | null
  role?: ProceedingRole | null
}

/** What a document says about itself and about the notice or order it answers. */
export interface DocRef {
  number?: string | null
  din?: string | null
  date?: string | null
  refers_to?: { number?: string | null; din?: string | null; date?: string | null }
}

export type ProceedingRole = "scn" | "reply" | "hearing" | "order" | "appeal" | "other"

/** One anchor document (a notice, or an order with no notice here) and the documents that belong to it. */
export interface Proceeding {
  id: string
  kind: "scn" | "order"
  number?: string
  din?: string
  date?: string | null
  /** e.g. "SCN 04/2024-25 · 16 Sep 2025". */
  title: string
  demand_total?: number | null
  members: { doc_id: string; role: ProceedingRole; date?: string | null; linked_by: "anchor" | "reference" | "user" | "only" }[]
}

/** A session that belongs to a Matter, listed in the Matter's contents. */
export interface MatterSession {
  type: MatterSessionType
  id: string
  title: string
  status: string
  started: boolean
  updated_at: string
  /** The server's feature tag, when it sends one. Null or absent: inferred from the session type. */
  feature?: FeatureTag | null
  /** The documents the session was started on (an order draft's selection, or the doc a reading was for). */
  selected_doc_ids?: string[] | null
}

export interface MatterContents {
  case: CaseFileRow
  sessions: MatterSession[]
  sessions_total: number
  files: MatterFile[]
  unavailable: string[]
  /** Absent from an older backend: the page then groups nothing. */
  proceedings?: Proceeding[]
  /** doc_ids of reply, hearing, order and appeal files the backend could not place. */
  unlinked?: string[]
}

/** What moving a session into a Matter would bring with it. */
export interface MovePreview {
  /** The Matter the session is in now, or null when it is in none. */
  in_case: string | null
  /** Shelf files of the target Matter that are not from this session. */
  matter_files: number
  /** Files the session itself carries. */
  session_files: number
}

/** What an allegation turns on (nira/law/evidence.py): the records an officer weighs on that kind of charge. */
export interface AllegationEvidence {
  kind: string
  label: string
  documents: { what: string; shows: string }[]
}

/** Where a question belongs (nira/agent/route.py): a notice or an order is read in its own door. */
export interface RouteView {
  to: "notice" | "appeal"
  why: "document" | "pasted"
  case_id: string | null
  doc_id: string | null
  filename: string
  message: string
}

export interface RouteDecision {
  route: RouteView | null
  /** An attached document is still being read: ask again once it is. */
  pending: boolean
}
